import { PageBackLink } from "@/components/page-back-link"
import { ListSearchFilters } from "@/components/list-search-filters"
import { PatientFiltersSchema } from "@optimist/schemas"
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { DataGridColumn } from "@/components/data-grid/data-grid"
import { DataGrid } from '@/components/data-grid'
import { Pagination } from '@/components/pagination'
import { ErrorBanner, ErrorBoundary } from '@/components/error-boundary'
import { PageBreadcrumbs, PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { usePortalAuth } from '@/features/authz/hooks/use-portal-auth'
import { getPatientPhiDisplayValue } from '@/features/patients/patient-detail-model'
import { queryKeys } from '@/lib/query-keys'
import { api } from '@/lib/api'
import { formatDate, formatDateTime } from '@/lib/utils'
import { getRowIndexProps } from '@/lib/pagination'
import type { components } from '@optimist/api-contracts'

/**
 * Patient row from the API
 */
interface PatientRow {
	id: string
	userId: string
	universalPatientId: string
	firstName?: string
	lastName?: string
	dateOfBirth?: string
	gender?: string
	email?: string
	phone?: string
	orderCount: number
	deactivatedAt: string | null
	createdAt: string
}

interface PatientListResponse {
	data: PatientRow[]
	meta: PaginationMeta
}
type PaginationMeta = components['schemas']['PaginationMeta']

/**
 * Patients Index page
 * Purpose: Find patients
 */
export const Route = createFileRoute('/dashboard/patients/')({
	component: PatientsPage,
	validateSearch: (search) => PatientFiltersSchema.parse(search),
})

function PatientsPage() {
	const navigate = useNavigate({ from: Route.fullPath })
	const filters = Route.useSearch()
	const { page, limit } = filters
	const authQuery = usePortalAuth()
	const permissions: readonly string[] = authQuery.data === undefined ? [] : authQuery.data.permissions
	const canReadPatientPhi = permissions.includes('patients.read.phi')

	const { data, isLoading, error, refetch } = useQuery({
		queryKey: queryKeys.patients.list(filters),
		queryFn: () => api.get<PatientListResponse>('/patients', { params: filters }),
	})

	const onRowClicked = (row: PatientRow) => {
    navigate({ to: '/dashboard/patients/$patientId', params: { patientId: row.id } })
  }

	const columnDefs: DataGridColumn<PatientRow>[] = [
		{
			field: 'id',
			headerName: 'ID',
			render: (row) => (
				<span className="font-mono text-meta">{row.id ? String(row.id).slice(0, 8) : ''}</span>
			),
		},
		{
			headerName: 'Name',
			render: (row) => {

				const firstName = row && row.firstName ? row.firstName : ''
				const lastName = row && row.lastName ? row.lastName : ''
				const fullName = `${firstName} ${lastName}`.trim()
				return fullName || '-'
			},
		},
		{
			field: 'email',
			headerName: 'Email',
			render: (row) => getPatientPhiDisplayValue({
				canReadPatientPhi,
				value: typeof row.email === 'string' ? row.email : undefined,
			}),
		},
		{
			field: 'phone',
			headerName: 'Phone',
			render: (row) => getPatientPhiDisplayValue({
				canReadPatientPhi,
				value: typeof row.phone === 'string' ? row.phone : undefined,
			}),
		},
		{
			field: 'dateOfBirth',
			headerName: 'DOB',
			render: (row) => getPatientPhiDisplayValue({
				canReadPatientPhi,
				value: typeof row.dateOfBirth === 'string' ? row.dateOfBirth : undefined,
				format: formatDate,
			}),
		},
		{
			field: 'orderCount',
			headerName: 'Orders',
			render: (row) => {
				const count = row.orderCount
				return (
					<span className={count > 0 ? 'text-primary font-medium' : 'text-muted-foreground'}>
						{count}
					</span>
				)
			},
		},
		{
			field: 'deactivatedAt',
			headerName: 'Status',
			render: (row) => (
				<StatusBadge variant={row.deactivatedAt === null ? 'success' : 'warning'}>
					{row.deactivatedAt === null ? 'Active' : 'Deactivated'}
				</StatusBadge>
			),
		},
		{
			field: 'createdAt',
			headerName: 'Created',
			render: (row) => (row.createdAt ? formatDateTime(row.createdAt) : '-'),
		},
	]

	const response = data
	const patients = response ? response.data : []
	const meta = response ? response.meta : null
	const rowIndexProps = getRowIndexProps({ meta, page, limit, mode: 'descending' })
	const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
	const handleRetry = () => {
		void refetch()
	}

	return (
		<ErrorBoundary
			title="Patients page error"
			fallback={<ErrorBanner title="Patients unavailable" message="Something went wrong loading this page." />}
		>
			<div className="space-y-6">
				<PageHeader back={<PageBackLink to="/dashboard" label="Back to dashboard" />}
					breadcrumbs={
						<PageBreadcrumbs
							items={[
								<Link key="dashboard" to="/dashboard">Dashboard</Link>,
								<span key="patients">Patients</span>,
							]}
						/>
					}
					title="Patients"
				/>

        <ListSearchFilters
          key={JSON.stringify([filters.search, filters.status])}
          title="Search and filter patients"
          placeholder="Patient ID or name"
          search={filters.search}
          status={filters.status}
          statuses={[{ value: "active", label: "Active" }, { value: "deactivated", label: "Deactivated" }]}
          onApply={(next) => navigate({ search: PatientFiltersSchema.parse({ ...next, page: 1, limit }) })}
          onClear={() => navigate({ search: { page: 1, limit } })}
        />

				{error ? (
					<ErrorBanner title="Error loading patients" message={errorMessage} onRetry={handleRetry} />
				) : null}

				<ErrorBoundary
					resetKey={patients.length}
					fallback={<ErrorBanner title="Patients table failed" message="Unable to render patient data." />}
				>
					<DataGrid<PatientRow>
						rowData={patients}
						columnDefs={columnDefs}
						loading={isLoading}
						{...rowIndexProps}
						onRowClicked={onRowClicked}
					/>
				</ErrorBoundary>

				{meta ? (
					<Pagination
						page={meta.page}
						limit={meta.limit}
						total={meta.total}
						totalPages={meta.totalPages}
						onPageChange={(nextPage) => {
							navigate({
								search: (prev) => ({
									...prev,
									page: nextPage,
									limit,
								}),
							})
						}}
						onLimitChange={(nextLimit) => {
							navigate({
								search: (prev) => ({
									...prev,
									page: 1,
									limit: nextLimit,
								}),
							})
						}}
					/>
				) : null}
			</div>
		</ErrorBoundary>
	)
}
